// Step 5: SLA & Follow-Up Defaults

import { useState } from 'react';
import { Clock, Bell, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import type { WizardStepProps } from '@/lib/onboarding/types';
import { useClientOnboarding } from '@/hooks/useClientOnboarding';
import { useClientSettings } from '@/hooks/useClientSettings';

export function Step5SLADefaults({ clientId, progress, onComplete, onBack }: WizardStepProps) {
  const { completeStep, isCompleting } = useClientOnboarding(clientId);
  const { settings, updateSettings, isUpdating } = useClientSettings(clientId);
  
  // Default SLA values
  const [defaultSLA, setDefaultSLA] = useState(settings?.sla_hours_default || 72);
  const [urgentSLA, setUrgentSLA] = useState(settings?.urgent_sla_hours_default || 24);
  const [followUpCadence, setFollowUpCadence] = useState(settings?.follow_up_cadence_hours_default || 48);
  const [escalationDays, setEscalationDays] = useState(5);
  
  // Per-payer overrides
  const [payerOverrides, setPayerOverrides] = useState<Record<string, { sla: number; followUp: number }>>({});

  const togglePayerOverride = (payerId: string) => {
    setPayerOverrides(prev => {
      if (prev[payerId]) {
        const { [payerId]: removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [payerId]: { sla: defaultSLA, followUp: followUpCadence },
      };
    });
  };

  const updatePayerOverride = (payerId: string, field: 'sla' | 'followUp', value: number) => {
    setPayerOverrides(prev => ({
      ...prev,
      [payerId]: {
        ...prev[payerId],
        [field]: value,
      },
    }));
  };

  const formatHours = (hours: number) => {
    if (hours < 24) return `${hours} hours`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    if (remainingHours === 0) return `${days} day${days > 1 ? 's' : ''}`;
    return `${days}d ${remainingHours}h`;
  };

  const handleSubmit = async () => {
    // Save settings to client_settings table
    updateSettings({
      sla_hours_default: defaultSLA,
      urgent_sla_hours_default: urgentSLA,
      follow_up_cadence_hours_default: followUpCadence,
    });

    completeStep({
      step: 5,
      stepData: {
        sla_defaults_configured: true,
      },
    }, {
      onSuccess: onComplete,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>SLA & Follow-Up Defaults</CardTitle>
        <CardDescription>
          Configure timing defaults for authorization processing and follow-ups.
          These can be overridden per payer.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Global Defaults */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Global Defaults
          </h3>

          {/* Default SLA */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Default SLA (Standard Cases)</Label>
              <Badge variant="outline">{formatHours(defaultSLA)}</Badge>
            </div>
            <Slider
              value={[defaultSLA]}
              onValueChange={([v]) => setDefaultSLA(v)}
              min={24}
              max={168}
              step={12}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Maximum time to submit authorization to payer
            </p>
          </div>

          {/* Urgent SLA */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                Urgent SLA
                <AlertTriangle className="h-4 w-4 text-orange-500" />
              </Label>
              <Badge variant="outline">{formatHours(urgentSLA)}</Badge>
            </div>
            <Slider
              value={[urgentSLA]}
              onValueChange={([v]) => setUrgentSLA(v)}
              min={4}
              max={48}
              step={4}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              SLA for cases marked as urgent
            </p>
          </div>

          {/* Follow-up Cadence */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                Follow-Up Cadence
                <Bell className="h-4 w-4" />
              </Label>
              <Badge variant="outline">{formatHours(followUpCadence)}</Badge>
            </div>
            <Slider
              value={[followUpCadence]}
              onValueChange={([v]) => setFollowUpCadence(v)}
              min={24}
              max={120}
              step={12}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              How often to follow up on pending authorizations
            </p>
          </div>

          {/* Escalation Threshold */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Escalation Threshold</Label>
              <Badge variant="destructive">{escalationDays} days</Badge>
            </div>
            <Slider
              value={[escalationDays]}
              onValueChange={([v]) => setEscalationDays(v)}
              min={3}
              max={14}
              step={1}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Days overdue before automatic escalation alert
            </p>
          </div>
        </div>

        <Separator />

        {/* Per-Payer Overrides */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Payer-Specific Overrides</h3>
          <p className="text-sm text-muted-foreground">
            Optionally set different SLA and follow-up times per payer.
          </p>

          <div className="space-y-3">
            {progress.selected_payers.map(payer => {
              const hasOverride = !!payerOverrides[payer.payer_id];
              const override = payerOverrides[payer.payer_id];

              return (
                <div
                  key={payer.payer_id}
                  className="p-4 border rounded-lg space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{payer.payer_name}</span>
                    <Button
                      variant={hasOverride ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => togglePayerOverride(payer.payer_id)}
                    >
                      {hasOverride ? 'Remove Override' : 'Add Override'}
                    </Button>
                  </div>

                  {hasOverride && override && (
                    <div className="grid gap-4 md:grid-cols-2 ml-4">
                      <div className="space-y-2">
                        <Label className="text-sm">SLA Hours</Label>
                        <Input
                          type="number"
                          value={override.sla}
                          onChange={(e) => updatePayerOverride(
                            payer.payer_id, 
                            'sla', 
                            parseInt(e.target.value) || defaultSLA
                          )}
                          min={12}
                          max={168}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Follow-Up Hours</Label>
                        <Input
                          type="number"
                          value={override.followUp}
                          onChange={(e) => updatePayerOverride(
                            payer.payer_id, 
                            'followUp', 
                            parseInt(e.target.value) || followUpCadence
                          )}
                          min={12}
                          max={120}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            These defaults apply to all new cases. Individual cases can have their 
            SLA adjusted by LINK Ops as needed.
          </AlertDescription>
        </Alert>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isCompleting || isUpdating}
          >
            {isCompleting ? 'Saving...' : 'Continue to Review'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
